import { registerSchema } from "@/api/schemas/auth.schema";
import { Symbol } from "@/components/fornada/brand";
import { TextField } from "@/components/fornada/controls";
import { Display, Label } from "@/components/fornada/primitives";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/providers/auth.provider";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { Link, Navigate } from "react-router-dom";
import { z } from "zod";

const signUpSchema = registerSchema
  .extend({ confirmPassword: z.string("Campo obrigatório!") })
  .refine((d) => d.password === d.confirmPassword, {
    message: "As senhas não conferem.",
    path: ["confirmPassword"],
  });

type SignUpValues = z.infer<typeof signUpSchema>;

const RegisterPage = () => {
  const { signUp, user, isLoading } = useAuth();
  const form = useForm<SignUpValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { name: "", email: "", password: "", confirmPassword: "" },
  });

  if (user) return <Navigate to="/" replace />;

  return (
    <div className="flex min-h-screen w-full flex-col px-5 pt-3 pb-6">
      <header className="mx-auto flex w-full max-w-md items-center justify-between">
        <Link
          to="/sign-in"
          aria-label="Voltar"
          className="-ml-2.5 flex size-11 items-center justify-center rounded-md text-fx-body hover:bg-fx-raised"
        >
          <ArrowLeft size={22} strokeWidth={1.6} />
        </Link>
        <Symbol size={28} />
      </header>

      <form
        onSubmit={form.handleSubmit(({ confirmPassword: _, ...data }) => signUp(data))}
        className="mx-auto mt-6 flex w-full max-w-md flex-col gap-3"
      >
        <Label accent className="text-[10.5px] tracking-[1.5px]">
          Passo único
        </Label>
        <Display size="xl">Criar conta</Display>
        <p className="mb-3 text-sm leading-5 text-fx-muted">
          Preencha seus dados para vender na campanha.
        </p>

        <Controller
          name="name"
          control={form.control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Nome completo"
              autoComplete="name"
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="email"
          control={form.control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="E-mail"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              placeholder="seu@email.com"
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="password"
          control={form.control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Senha"
              type="password"
              autoComplete="new-password"
              placeholder="Mínimo de 8 caracteres"
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="confirmPassword"
          control={form.control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Confirmar senha"
              type="password"
              autoComplete="new-password"
              error={fieldState.error?.message}
            />
          )}
        />

        <Button type="submit" size="xl" className="mt-2 w-full" disabled={isLoading}>
          Criar conta
        </Button>
        <p className="text-center text-[13px] text-fx-secondary">
          Já tem conta?{" "}
          <Link to="/sign-in" className="font-semibold text-fx-cream hover:text-fx-cream-hover">
            Entrar
          </Link>
        </p>
      </form>
    </div>
  );
};

export default RegisterPage;
